import React, { useState, useEffect, useRef } from "react";
import { Outlet, Navigate, NavLink, useNavigate, useLocation } from "react-router-dom";
import TopBar from "@/components/layout/TopBar";
import Sidebar from "@/components/layout/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PlusCircle, Loader, Search, Settings,
  LayoutDashboard, Receipt,
} from "lucide-react";

// How often sidebar badges (new approval requests, newly-overdue invoices)
// are polled — frequent enough to notice within a coffee break, light enough
// not to hammer the API.
const BADGE_POLL_MS = 30000;

// Client-facing status labels for the job-status-changed toast — keep in
// sync with STATUS_META in JobStatus.jsx / STATUS in ClientJobs.jsx.
const CLIENT_STATUS_LABELS = {
  needs_approval: "Pending Approval",
  assigned: "Assigned",
  pending: "Assigned",
  onboard: "Onboard",
  done: "Delivered",
  futile: "Futile",
};

const ADMIN_BOTTOM_NAV = [
  { to: "/dashboard/home",        label: "Dashboard", icon: LayoutDashboard },
  { to: "/dashboard/create-job",  label: "Create",    icon: PlusCircle },
  { to: "/dashboard/job-status",  label: "Status",    icon: Loader },
  { to: "/dashboard/invoicing",   label: "Invoicing", icon: Receipt },
];

// Allocator is a restricted staff role — Create Job, Job Status and Job
// History only. No Dashboard (financial widgets) or Invoicing tab.
const ALLOCATOR_BOTTOM_NAV = [
  { to: "/dashboard/create-job",  label: "Create",    icon: PlusCircle },
  { to: "/dashboard/job-status",  label: "Status",    icon: Loader },
  { to: "/dashboard/job-history", label: "History",   icon: Search },
  { to: "/dashboard/settings",    label: "Settings",  icon: Settings },
];

// Route prefixes an Allocator may reach — anything else under /dashboard
// (Dashboard home, Invoicing, Financials, Approvals, Team, etc.) redirects away.
const ALLOCATOR_ALLOWED_PREFIXES = [
  "/dashboard/create-job",
  "/dashboard/job-status",
  "/dashboard/job-history",
  "/dashboard/settings",
];

const CLIENT_BOTTOM_NAV = [
  { to: "/dashboard/job-status",  label: "Status",   icon: Loader },
  { to: "/dashboard/job-history", label: "History",  icon: Search },
  { to: "/dashboard/new-request", label: "New Job",  icon: PlusCircle },
  { to: "/dashboard/settings",    label: "Settings", icon: Settings },
];

export default function DashboardLayout() {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [overdueInvoices, setOverdueInvoices] = useState(0);
  const prevApprovalsRef = useRef(null);
  const firstJobPollRef = useRef(true);

  // Poll for new client job requests so admins get a badge + toast instead of
  // having to keep clicking into Approvals to check.
  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/jobs/pending-approval-count");
        if (cancelled) return;
        const count = data?.count || 0;
        if (prevApprovalsRef.current !== null && count > prevApprovalsRef.current) {
          const added = count - prevApprovalsRef.current;
          toast.info(`${added} new job request${added !== 1 ? "s" : ""} awaiting approval`, {
            action: { label: "View", onClick: () => navigate("/dashboard/approvals") },
          });
        }
        prevApprovalsRef.current = count;
        setPendingApprovals(count);
      } catch { /* silent — non-critical background poll */ }
    };
    poll();
    const interval = setInterval(poll, BADGE_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role, navigate]);

  // Poll for invoices that have crossed their due date — badges the
  // Invoicing tab for both admin (all customers) and client (their own).
  // Allocators have no Invoicing tab and no billing access, so skip too.
  useEffect(() => {
    if (!user || user.role === "driver" || user.role === "allocator") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/invoices/overdue-count");
        if (!cancelled) setOverdueInvoices(data?.count || 0);
      } catch { /* silent — non-critical background poll */ }
    };
    poll();
    const interval = setInterval(poll, BADGE_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role]);

  // Poll for job status changes so clients get an in-app toast instead of
  // having to keep refreshing Job Status to notice. Skips the first poll
  // (which is just "seeding" last-seen status for every job already on the
  // list) so login doesn't spam a toast per job.
  useEffect(() => {
    if (user?.role !== "client") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/jobs");
        if (cancelled) return;
        const isFirstPoll = firstJobPollRef.current;
        for (const j of data) {
          const key = `job_status_seen_${j.id}`;
          const prevStatus = localStorage.getItem(key);
          if (!isFirstPoll && prevStatus && prevStatus !== j.status) {
            const label = CLIENT_STATUS_LABELS[j.status] || j.status;
            toast.info(`${j.job_ref || "Job"}: ${label}`, {
              action: { label: "View", onClick: () => navigate("/dashboard/job-status") },
            });
          }
          localStorage.setItem(key, j.status);
        }
        firstJobPollRef.current = false;
      } catch { /* silent — non-critical background poll */ }
    };
    poll();
    const interval = setInterval(poll, BADGE_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] text-white/60">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "driver") return <Navigate to="/driver-dashboard/runs" replace />;
  // Allocator route guard — hard-block anything outside Create Job / Job
  // Status / Job History / Settings (Financials, Invoicing, Team, Approvals,
  // Dashboard home, etc. are never reachable, not just hidden from nav).
  if (
    user.role === "allocator" &&
    !ALLOCATOR_ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/dashboard/job-status" replace />;
  }

  const bottomNav = user.role === "admin" ? ADMIN_BOTTOM_NAV
    : user.role === "allocator" ? ALLOCATOR_BOTTOM_NAV
    : CLIENT_BOTTOM_NAV;

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar onMenuClick={() => setSidebarOpen(true)} />

      <div className="flex flex-1">
        {/* Desktop sidebar (always visible ≥ lg) */}
        <div className="hidden lg:block">
          <Sidebar pendingApprovals={pendingApprovals} overdueInvoices={overdueInvoices} />
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="lg:hidden">
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="sidebar-drawer df-sidebar open" style={{ paddingTop: "calc(16px + env(safe-area-inset-top, 0px))" }}>
              <Sidebar onClose={() => setSidebarOpen(false)} pendingApprovals={pendingApprovals} overdueInvoices={overdueInvoices} />
            </div>
          </div>
        )}

        <main
          className={`flex-1 overflow-x-hidden app-content ${theme === "light" ? "theme-light" : ""}`}
          data-testid="dashboard-main"
        >
          <div className="p-4 sm:p-6 md:p-10 fade-up admin-content-pad">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav (hidden on lg+) */}
      <nav className="mobile-bottom-nav lg:hidden" data-testid="admin-mobile-nav">
        {bottomNav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `mobile-bottom-nav-item ${isActive ? "admin-active" : ""}`
            }
          >
            <Icon size={22} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
